/** How a stroke is broken up; each pattern alternates dash and gap lengths, solid has none. */
export const LINE_TYPES = {
  solid: { dxf: 'CONTINUOUS', pattern: [] },
  dashed: { dxf: 'DASHED', pattern: [6, 3] },
  dotted: { dxf: 'DOT', pattern: [1, 3] },
  center: { dxf: 'CENTER', pattern: [12, 3, 3, 3] },
} as const satisfies Record<string, { dxf: string; pattern: readonly number[] }>;

export type LineType = keyof typeof LINE_TYPES;

/** How a dimension line ends: a filled arrowhead, an open one, or an oblique tick. */
export const ARROW_TYPES = ['closed', 'open', 'tick'] as const;

export type ArrowType = (typeof ARROW_TYPES)[number];

/**
 * How an object looks. Every property is optional: an object's own value wins, then its
 * layer's, then the surface's default. Text height and arrows size dimensions.
 */
export interface Style {
  /** `#rrggbb`. */
  readonly color?: string;
  /** Stroke thickness in drawing units; the canvas never draws it thinner than a pixel. */
  readonly thick?: number;
  readonly line?: LineType;
  /** A dimension's text height, in drawing units. */
  readonly textHeight?: number;
  /** A dimension's arrowhead length, in drawing units. */
  readonly arrowSize?: number;
  readonly arrow?: ArrowType;
}

/** Every style property, in the order panels and replies show them. */
export const STYLE_KEYS = [
  'color',
  'thick',
  'line',
  'textHeight',
  'arrowSize',
  'arrow',
] as const satisfies readonly (keyof Style)[];

const COLOR = /^#[0-9a-f]{6}$/i;

export const isLineType = (value: unknown): value is LineType =>
  typeof value === 'string' && Object.hasOwn(LINE_TYPES, value);

export const isArrowType = (value: unknown): value is ArrowType =>
  (ARROW_TYPES as readonly unknown[]).includes(value);

const positive = (value: unknown): value is number =>
  typeof value === 'number' && value > 0 && value !== Infinity;

/**
 * Checks a style from outside, such as a script's output or a stored layer, and keeps only
 * what it knows.
 */
export function cleanStyle(value: unknown): Style {
  if (typeof value !== 'object' || value === null) throw new Error('A style must be an object');
  const record = value as Record<string, unknown>;
  // Deprecated: `width` is read as `thick` for drawings saved before the rename.
  const thick = record['thick'] ?? record['width'];
  const { color, line, textHeight, arrowSize, arrow } = record;
  if (color !== undefined && (typeof color !== 'string' || !COLOR.test(color)))
    throw new Error('A style color must be #rrggbb');
  if (thick !== undefined && !positive(thick))
    throw new Error('A style thick must be a positive number');
  if (line !== undefined && !isLineType(line))
    throw new Error(`A style line must be one of ${Object.keys(LINE_TYPES).join(', ')}`);
  if (textHeight !== undefined && !positive(textHeight))
    throw new Error('A style textHeight must be a positive number');
  if (arrowSize !== undefined && !positive(arrowSize))
    throw new Error('A style arrowSize must be a positive number');
  if (arrow !== undefined && !isArrowType(arrow))
    throw new Error(`A style arrow must be one of ${ARROW_TYPES.join(', ')}`);
  return {
    ...(color === undefined ? {} : { color: color.toLowerCase() }),
    ...(thick === undefined ? {} : { thick }),
    ...(line === undefined ? {} : { line }),
    ...(textHeight === undefined ? {} : { textHeight }),
    ...(arrowSize === undefined ? {} : { arrowSize }),
    ...(arrow === undefined ? {} : { arrow }),
  };
}

export const sameStyle = (a: Style, b: Style): boolean =>
  STYLE_KEYS.every((key) => a[key] === b[key]);

/** Whether the style sets anything at all. */
export const hasStyle = (style: Style): boolean =>
  STYLE_KEYS.some((key) => style[key] !== undefined);

/** `over`'s properties on top of `base`'s; either may be missing. */
export function mergeStyle(base: Style | undefined, over: Style | undefined): Style | undefined {
  if (!over) return base;
  if (!base) return over;
  return { ...base, ...over };
}
